/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { atom } from 'jotai'

/** The structure for the session atom. */
type SessionAtom = {
  /** The access token for the user. */
  accessToken: string

  /** The user's ID. */
  userId: string

  /** The user's email. */
  email: string

  /** The user's first name. */
  firstName: string

  /** The user's last name. */
  lastName: string

  /** The user's company. */
  company?: string

  /** The user's phone number. */
  phone?: string
}

// Core authentication atoms
const accessTokenAtom = atom<string | undefined>(undefined)
const userIdAtom = atom<string | undefined>(undefined)

// Basic user info atoms
const emailAtom = atom<string | undefined>(undefined)
const firstNameAtom = atom<string | undefined>(undefined)
const lastNameAtom = atom<string | undefined>(undefined)

// Optional user info atoms
const companyAtom = atom<string | undefined>(undefined)
const phoneAtom = atom<string | undefined>(undefined)

/** Derived atom for the user's full name. */
const fullNameAtom = atom<string | undefined>((get) => {
  const firstName = get(firstNameAtom)
  const lastName = get(lastNameAtom)
  if (!firstName && !lastName) return undefined
  return [firstName, lastName].filter(Boolean).join(' ')
})

/** Derived atom for the user's initials. */
const initialsAtom = atom<string | undefined>((get) => {
  const firstName = get(firstNameAtom)
  const lastName = get(lastNameAtom)
  if (!firstName || !lastName) return undefined

  return [firstName, lastName]
    .filter(Boolean)
    .map((name) => name.charAt(0).toUpperCase())
    .join('')
})

/** Composite atom for the user's session. */
const sessionAtom = atom<SessionAtom | undefined, [SessionAtom | undefined], void>(
  (get) => {
    const accessToken = get(accessTokenAtom)
    const userId = get(userIdAtom)
    const email = get(emailAtom)
    const firstName = get(firstNameAtom)
    const lastName = get(lastNameAtom)
    if (!accessToken || !userId || !email || !firstName || !lastName) return undefined

    return {
      accessToken,
      userId,
      email,
      firstName,
      lastName,
      company: get(companyAtom),
      phone: get(phoneAtom),
    }
  },
  (_get, set, newSession: SessionAtom | undefined) => {
    if (!newSession) {
      // Clear all atoms
      set(accessTokenAtom, undefined)
      set(userIdAtom, undefined)
      set(emailAtom, undefined)
      set(firstNameAtom, undefined)
      set(lastNameAtom, undefined)
      set(companyAtom, undefined)
      set(phoneAtom, undefined)

      return
    }

    // Update individual atoms
    set(accessTokenAtom, newSession.accessToken)
    set(userIdAtom, newSession.userId)
    set(emailAtom, newSession.email)
    set(firstNameAtom, newSession.firstName)
    set(lastNameAtom, newSession.lastName)
    set(companyAtom, newSession.company)
    set(phoneAtom, newSession.phone)
  },
)

/** Atom debug labels. */
accessTokenAtom.debugLabel = 'accessTokenAtom'
userIdAtom.debugLabel = 'userIdAtom'
emailAtom.debugLabel = 'emailAtom'
firstNameAtom.debugLabel = 'firstNameAtom'
lastNameAtom.debugLabel = 'lastNameAtom'
companyAtom.debugLabel = 'companyAtom'
phoneAtom.debugLabel = 'phoneAtom'
fullNameAtom.debugLabel = 'fullNameAtom'
initialsAtom.debugLabel = 'initialsAtom'
sessionAtom.debugLabel = 'sessionAtom'

export {
  accessTokenAtom,
  userIdAtom,
  emailAtom,
  firstNameAtom,
  lastNameAtom,
  companyAtom,
  phoneAtom,
  fullNameAtom,
  initialsAtom,
  sessionAtom,
}
export type { SessionAtom }
